/**
 * NIGHT OWL - Real-time Training Metrics Dashboard
 *
 * Displays comprehensive training metrics with live charts:
 * - Global loss and accuracy curves
 * - Neurosymbolic fusion score
 * - Knowledge graph growth
 * - Learning rate schedule
 * - Agent-specific metrics
 */

import { useState, useEffect } from 'react';
import {
  LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { getMockDataGenerator, TrainingMetrics } from '../data/mockTrainingData';

export default function MetricsDashboard() {
  const dataGen = getMockDataGenerator();
  const [metricsHistory, setMetricsHistory] = useState<TrainingMetrics[]>([]);
  const [latestMetrics, setLatestMetrics] = useState<TrainingMetrics | null>(null);
  const [trainingDuration, setTrainingDuration] = useState<string>('00:00:00');

  useEffect(() => {
    const interval = setInterval(() => {
      setMetricsHistory([...dataGen.getMetricsHistory()]);
      setLatestMetrics(dataGen.getLatestMetrics());
      setTrainingDuration(dataGen.getTrainingDuration());
    }, 110); // 10% slower tempo

    return () => clearInterval(interval);
  }, []);

  if (!latestMetrics) {
    return null;
  }

  // Format chart data (last 100 points for performance)
  const chartData = metricsHistory.slice(-100).map((m) => ({
    epoch: m.globalEpoch,
    loss: m.globalLoss,
    accuracy: m.globalAccuracy * 100,
    fusionScore: m.fusionScore * 100,
    neuralRecall: m.neuralRecall * 100,
    symbolicPrecision: m.symbolicPrecision * 100,
    learningRate: m.learningRate * 10000,  // Scale for visibility
  }));

  return (
    <div className="glass-panel p-4">
      <div className="grid grid-cols-6 gap-4 mb-4">
        {/* Key metrics cards */}
        <MetricCard
          label="Global Epoch"
          value={latestMetrics.globalEpoch}
          format="number"
          color="text-blue-400"
        />

        <MetricCard
          label="Global Loss"
          value={latestMetrics.globalLoss}
          format="decimal"
          color="text-red-400"
          trend={metricsHistory.length > 1 ? latestMetrics.globalLoss - metricsHistory[metricsHistory.length - 2].globalLoss : 0}
        />

        <MetricCard
          label="Accuracy"
          value={latestMetrics.globalAccuracy * 100}
          format="percent"
          color="text-green-400"
          trend={metricsHistory.length > 1 ? (latestMetrics.globalAccuracy - metricsHistory[metricsHistory.length - 2].globalAccuracy) * 100 : 0}
        />

        <MetricCard
          label="Fusion Score"
          value={latestMetrics.fusionScore * 100}
          format="percent"
          color="text-purple-400"
        />

        <MetricCard
          label="Knowledge Graph"
          value={latestMetrics.knowledgeGraphSize}
          format="number"
          color="text-cyan-400"
          suffix=" nodes"
        />

        <MetricCard
          label="Training Time"
          value={trainingDuration}
          format="string"
          color="text-yellow-400"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-3 gap-4">
        {/* Loss and Accuracy */}
        <div className="glass-panel p-3">
          <h3 className="text-xs font-mono text-gray-400 mb-2 uppercase">Loss & Accuracy</h3>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="epoch"
                stroke="#6b7280"
                fontSize={10}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                stroke="#ef4444"
                fontSize={10}
                tickLine={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#10b981"
                fontSize={10}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  fontSize: '11px'
                }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="loss"
                stroke="#ef4444"
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="accuracy"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Neurosymbolic Fusion */}
        <div className="glass-panel p-3">
          <h3 className="text-xs font-mono text-gray-400 mb-2 uppercase">Neurosymbolic Fusion</h3>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="epoch"
                stroke="#6b7280"
                fontSize={10}
                tickLine={false}
              />
              <YAxis
                stroke="#6b7280"
                fontSize={10}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  fontSize: '11px'
                }}
              />
              <Area
                type="monotone"
                dataKey="fusionScore"
                stroke="#d946ef"
                fill="#d946ef"
                fillOpacity={0.3}
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="neuralRecall"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.2}
                strokeWidth={1.5}
              />
              <Area
                type="monotone"
                dataKey="symbolicPrecision"
                stroke="#a855f7"
                fill="#a855f7"
                fillOpacity={0.2}
                strokeWidth={1.5}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Learning Rate Schedule */}
        <div className="glass-panel p-3">
          <h3 className="text-xs font-mono text-gray-400 mb-2 uppercase">Learning Rate</h3>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="epoch"
                stroke="#6b7280"
                fontSize={10}
                tickLine={false}
              />
              <YAxis
                stroke="#6b7280"
                fontSize={10}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  fontSize: '11px'
                }}
                formatter={(value: any) => [(value / 10000).toExponential(2), 'LR']}
              />
              <Line
                type="monotone"
                dataKey="learningRate"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// Metric card component
interface MetricCardProps {
  label: string;
  value: number | string;
  format: 'number' | 'decimal' | 'percent' | 'string';
  color: string;
  trend?: number;
  suffix?: string;
}

function MetricCard({ label, value, format, color, trend, suffix = '' }: MetricCardProps) {
  let displayValue: string;

  if (format === 'number') {
    displayValue = typeof value === 'number' ? value.toLocaleString() : String(value);
  } else if (format === 'decimal') {
    displayValue = typeof value === 'number' ? value.toFixed(4) : String(value);
  } else if (format === 'percent') {
    displayValue = typeof value === 'number' ? `${value.toFixed(1)}%` : String(value);
  } else {
    displayValue = String(value);
  }

  return (
    <div className="text-center">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-mono">
        {label}
      </div>
      <div className={`text-xl font-bold font-mono ${color} tabular-nums`}>
        {displayValue}{suffix}
      </div>
      {trend !== undefined && trend !== 0 && (
        <div className={`text-[10px] font-mono mt-0.5 ${trend < 0 ? 'text-green-400' : 'text-red-400'}`}>
          {trend > 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(4)}
        </div>
      )}
    </div>
  );
}
